import { MoveShort } from "@chesshotel/chess";
import { getPieceId } from "../chessUtils/fen";
import {
  key2pos,
  allKeys,
  distance,
  containsX,
  getOffsetProc,
  Point,
} from "../chessUtils/util";
import { AnimMove, Board, Key, Piece, Pos } from "./types";
// import { addSound } from "./Board";

export const ANIMATION_DURATION = 370;
export const Z_OVER = 30;
export const Z_UNDER = 20;
export const Z_FADE_OUT = 19;

function makePiece(k: Key, piece: Partial<Piece>): Piece {
  return {
    key: k,
    pos: key2pos(k) as Pos,
    role: piece.role,
    color: piece.color,
    id: piece.id,
  };
}

function samePiece(p1: Piece, p2: Piece) {
  return p1.role === p2.role && p1.color === p2.color;
}

function closer(piece: Piece, pieces: Piece[]) {
  return pieces.sort(function (p1, p2) {
    return distance(piece.pos, p1.pos) - distance(piece.pos, p2.pos);
  })[0];
}

export function computeAnims(
  prev: Board,
  current: Board,
  playingWhite: boolean
) {
  let moves: Record<string, AnimMove> = {},
    movesOrigs: Key[] = [],
    fadeIns: Piece[] = [],
    fadeOuts: Piece[] = [],
    missings: Piece[] = [],
    news: Piece[] = [],
    prePieces: Board = {},
    curPieces: Board = {},
    piece: Piece,
    hasCapture = false,
    toBoard: Board = {},
    promotion: null | {
      type: "forward" | "backward";
      from: Key;
      to: Key;
      newRole: Piece["role"];
    } = null;

  // create previous pieces
  for (let pk in prev) {
    piece = makePiece(pk as Key, prev[pk]);
    prePieces[piece.key] = piece;
  }

  // create current pieces
  for (let ck in current) {
    piece = makePiece(ck as Key, current[ck]);
    curPieces[piece.key] = piece;
  }

  // missings are pieces gone from the board
  // news are pieces added to the board
  // toBoard is the new board. it starts out with the unchanged pieces,
  for (let i = 0; i < allKeys.length; i++) {
    let key = allKeys[i];
    let curP = curPieces[key];
    let preP = prePieces[key];
    if (curP) {
      if (preP) {
        if (!samePiece(curP, preP)) {
          missings.push(preP);
          news.push(makePiece(key as Key, curP));
        } else {
          toBoard[key] = preP;
        }
      } else news.push(makePiece(key as Key, curP));
    } else if (preP) missings.push(preP);
  }

  function addMove(preP: Piece, newP: Piece) {
    const from = preP.key;
    const to = newP.key;
    const id = prev[from].id;
    const start = getOffsetProc(from, playingWhite) as Point;
    const goal = getOffsetProc(to, playingWhite) as Point;
    const move: AnimMove = { start, goal, id, to, from };

    toBoard[to] = prev[from];
    if (prev[move.to]) {
      hasCapture = true;
    }

    moves[id] = move;
    movesOrigs.push(preP.key);
  }

  // All new pieces are either moves or pieces added to the board.
  news.forEach((newP) => {
    // Try standard same-piece pairing first
    let preP = closer(
      newP,
      missings.filter((p) => samePiece(newP, p))
    );

    // Helper to determine promotion targets
    function isPromotionTarget(role: Piece["role"]) {
      return (
        role === "queen" ||
        role === "rook" ||
        role === "bishop" ||
        role === "knight"
      );
    }

    // Detect forward promotion: new piece is a promotion target, missing is a pawn that could have promoted into this square
    if (!preP && isPromotionTarget(newP.role)) {
      const cand = closer(
        newP,
        missings.filter((p) => p.role === "pawn" && p.color === newP.color)
      );
      if (cand) {
        const newPos = key2pos(newP.key);
        const fromPos = key2pos(cand.key);
        const fileDelta = Math.abs(newPos[0] - fromPos[0]);
        const rankDelta = newPos[1] - fromPos[1];
        const isForwardWhite =
          newP.color === "white" && newPos[1] === 8 && rankDelta === 1;
        const isForwardBlack =
          newP.color === "black" && newPos[1] === 1 && rankDelta === -1;
        const captureLike = fileDelta === 0 || fileDelta === 1;
        if (
          (isForwardWhite || isForwardBlack) &&
          captureLike &&
          !containsX(movesOrigs, cand.key)
        ) {
          preP = cand;
          promotion = {
            type: "forward",
            from: cand.key,
            to: newP.key as Key,
            newRole: newP.role,
          };
        }
      }
    }

    // Detect backward (undo) promotion: new piece is a pawn, missing is a promotion target at the promotion rank
    if (!preP && newP.role === "pawn") {
      const cand = closer(
        newP,
        missings.filter(
          (p) => isPromotionTarget(p.role) && p.color === newP.color
        )
      );
      if (cand) {
        const toPos = key2pos(newP.key);
        const promPos = key2pos(cand.key);
        const fileDelta = Math.abs(promPos[0] - toPos[0]);
        const rankDelta = promPos[1] - toPos[1];
        const isBackWhite =
          newP.color === "white" && promPos[1] === 8 && rankDelta === 1;
        const isBackBlack =
          newP.color === "black" && promPos[1] === 1 && rankDelta === -1;
        const captureLike = fileDelta === 0 || fileDelta === 1;
        if (
          (isBackWhite || isBackBlack) &&
          captureLike &&
          !containsX(movesOrigs, cand.key)
        ) {
          preP = cand;
          promotion = {
            type: "backward",
            from: cand.key,
            to: newP.key as Key,
            newRole: cand.role,
          };
        }
      }
    }

    if (preP && !containsX(movesOrigs, preP.key)) {
      addMove(preP, newP);
      // If this is a backward (undo) promotion, ensure the moving piece renders as a pawn before animating
      if (
        promotion &&
        promotion.type === "backward" &&
        preP.key === promotion.from &&
        newP.key === promotion.to
      ) {
        // Override toBoard at destination to be a pawn with the same id/color as the source promoted piece
        const moving = prev[preP.key];
        toBoard[newP.key] = {
          ...moving,
          role: "pawn",
        } as any;
      }
    } else {
      fadeIns.push(newP);
    }
  });

  const pieceIds =
    fadeIns.length && Object.values(prev).map((piece: any) => piece.id);

  for (let i in fadeIns) {
    const fade = fadeIns[i];
    fade.id = getPieceId(pieceIds, fade.color + fade.role, 0);
    pieceIds.push(fade.id);
    toBoard[fade.key] = fade;
  }

  missings.forEach((p) => {
    if (!containsX(movesOrigs, p.key)) {
      fadeOuts.push(p);
    }
  });

  return {
    moves,
    fadeIns,
    fadeOuts,
    toBoard,
    hasCapture,
    promotion,
  };
}

// function easeInOutQuad(
//   elapsed: number,
//   initialValue: number,
//   goal: number,
//   duration: number
// ): number {
//   if ((elapsed /= duration / 2) < 1) {
//     return ((goal - initialValue) / 2) * elapsed * elapsed + initialValue;
//   }
//   return (
//     (-(goal - initialValue) / 2) * (--elapsed * (elapsed - 2) - 1) +
//     initialValue
//   );
// }

// function animMove(time, move) {
//   let x = easeInOutQuad(time, move.start.x, move.goal.x, ANIMATION_DURATION);
//   let y = easeInOutQuad(time, move.start.y, move.goal.y, ANIMATION_DURATION);
//   return { x, y };
// }

// function setZIndex(moveAnims, zIndex, pieceRefs) {
//   moveAnims.forEach((move) => {
//     if (move.id) {
//       const pieceNode = pieceRefs.current[move.id];
//       if (pieceNode) {
//         pieceNode.style.zIndex = zIndex;
//       }
//     }
//   });
// }

// function rafAnimation({
//   moves,
//   pieceRefs,
//   dispatch,
//   toBoard,
//   hasCapture,
//   animating,
// }) {
//   setZIndex(moves, Z_OVER, pieceRefs);

//   const startTime = performance.now();
//   loop();

//   function loop() {
//     if (!animating.current) {
//       addSound(hasCapture);
//       return;
//     }
//     const time = performance.now() - startTime;

//     if (time < ANIMATION_DURATION) {
//       moves.forEach((move) => {
//         if (move.id) {
//           const { x, y } = animMove(time, move);
//           const pieceNode = pieceRefs.current[move.id];
//           if (pieceNode) {
//             pieceNode.style.transform = `translate(${x}%, ${y}%)`;
//           }
//         }
//       });

//       requestAnimationFrame(loop);
//     } else {
//       addSound(hasCapture);
//       dispatch({ type: "setPosition", position: toBoard });
//     }
//   }
// }

// export function animatePosition(animProps) {
//   const { animating, animatingToBoard, dispatch } = animProps;
//   if (animating.current) {
//     animating.current = false;
//     dispatch({
//       type: "setPosition",
//       position: animatingToBoard.current,
//     });
//     animProps.position = animatingToBoard.current;
//     requestAnimationFrame(() => {
//       animatePieces(animProps);
//     });
//   } else {
//     animatePieces(animProps);
//   }
// }

// function animatePieces({
//   dispatch,
//   animating,
//   animatingToBoard,
//   pieceRefs,
//   anims,
//   position,
//   newPosition,
//   playingWhite,
//   lastMove,
// }) {
//   const { hasCapture, toBoard, fadeIns, moves } = computeAnims(
//     position,
//     newPosition,
//     playingWhite,
//     lastMove
//   );
//   animating.current = true;
//   animatingToBoard.current = toBoard;

//   const animParams = {
//     moves,
//     pieceRefs,
//     anims,
//     dispatch,
//     toBoard,
//     hasCapture,
//     animating,
//     lastMove,
//   };

//   if (fadeIns.length) {
//     dispatch({ type: "setNewPieces", newPieces: fadeIns });
//   }

//   if (typeof Element.prototype.animate == "function") {
//     animation(animParams);
//   } else {
//   rafAnimation(animParams);
//   }
// }

// function animation({ moves, pieceRefs, dispatch, toBoard, hasCapture, anims }) {
//   setZIndex(moves, Z_OVER, pieceRefs);

//   let didEnd = false;
//   moves.forEach((move) => {
//     if (move.id) {
//       const pieceNode = pieceRefs.current[move.id];

//       if (pieceNode) {
//         const anim = pieceNode.animate(
//           [
//             { transform: `translate(${move.start.x}%, ${move.start.y}%)` },
//             { transform: `translate(${move.goal.x}%, ${move.goal.y}%)` },
//           ],
//           {
//             duration: ANIMATION_DURATION,
//             easing: "ease-in-out",
//           }
//         );
//         anim.onfinish = () => {
//           pieceNode.style.transform = `translate(${move.goal.x}%, ${move.goal.y}%)`;

//           if (!didEnd) {
//             addSound(hasCapture);
//             dispatch({ type: "setPosition", position: toBoard });
//             didEnd = true;
//             delete anims[move.id];
//           }
//         };

//         anims[move.id] = anim;
//       }
//     }
//   });
// }
